import { FilterGroup } from 'src/app/modules/core/store/models/filters.model';
import { Document } from 'src/app/modules/core/store/models/document.model';

export interface OrganizationChannel {
  channelUId: string;
  channelType: ChannelType;
  channelTypeDisplay: string;
  name: string;
  description: string;
  logoImagePath: string;
}

export interface ConfiguredChannel {
  uId: string;
  channelUId: string;
  name: string;
  description: string;
  channelName: string;
  channelDescription: string;
  channelType: ChannelType;
  channelTypeDisplay: string;
  logoImagePath: string;
  isActive: boolean;
  settings: any;
  secureSettings: any;
  createdOn: any;
  updatedOn: any;
  lastEditedByName: string;
  lastEditedById: string;
}

export interface DistributionList {
  uId: string;
  name: string;
  description: string;
  channelType: ChannelType;
  channelUId: string;
  channelTypeDisplay?: string;
  nameFieldUId: string;
  destinationFieldUId: string;
  isActive: boolean;
  createdOn: any;
  updatedOn: any;
  lastEditedByName: string;
  lastEditedById: string;
  queryMatchRule: QueryMatchRule;
}

export interface QueryMatchRule {
  uId?: string | null;
  datasetUId?: string | null;
  root: FilterGroup | null;
}

export interface Template {
  uId?: string | null;
  name: string | null;
  description: string;
  channelType: ChannelType;
  channelUId?: string | null;
  channelTypeDisplay?: string;
  datasetUId: string;
  layout: string;
  isActive: boolean;
  createdOn: any;
  updatedOn: any;
  lastEditedByName: string;
  lastEditedById: string;
  settings: {
    toRecipients: any[];
    ccRecipients: any[];
    toReadOnly: boolean;
    ccReadOnly: boolean;
    bccRecipients: any[];
    subject: string;
    sendAsGroup: boolean;
    sendDocumentAttachments: boolean;
  };
}

export enum ChannelType {
  None = 0,
  Email = 1,
  Sms = 2,
  App = 3,
  MassNotifications = 4,
  Custom = 5
}

export const ChannelTypeIcons = {
  [ChannelType.Email]: 'icon-email',
  [ChannelType.Sms]: 'icon-satellite-dish',
  [ChannelType.App]: 'icon-small_comment',
  [ChannelType.MassNotifications]: 'icon-bullhorn',
  [ChannelType.Custom]: 'icon-email'
};

export const ChannelUIds = {
  TopoEmailChannelUId: '3f8b4b58-89ce-4d89-9b98-3ed09dfc77a7',
  SendGridEmailChannelUId: '6d101671-653f-481a-96cc-2e6bf672362c',
  SlackChannelUId: '5991b5a4-4ec4-4ca1-8ee1-b82350d70f33',
  EverbridgeChannelUId: '2c79fd53-42ad-4a07-84dd-27db322ffc96',
  MicrosoftTeamsChannelUId: 'f70a9cf7-6bac-497c-9c91-32d4bd5503ec',
  TwilioChannelUId: '03e3bda7-eb46-4cef-93d2-2fa0ac084324',
  TwilioTtsChannelUId: 'ba336b85-30ba-4751-a69a-1801d76f59f1',
  HorizonSmsChannelUId: 'ffcca0a6-e8c7-4712-bec2-c752aaa7ca87',
  HorizonMobilePushChannelUId: '835b85a4-1f27-47df-b229-bb232cb465f4',
  WhatsAppChannelUId: '87d7c379-0eba-42d8-81b4-ae60ed45af2c',
  GwtsMobileChannelUId: 'a887d7ab-f802-4622-a28d-8c18d4b7cbd0'
};

export enum TemplateControlType {
  None = 0,
  Image = 1,
  Text = 2,
  Divider = 3,
  Button = 4,
  Columns = 5,
  FormField = 6,
  Attachments = 7,
  Map = 8,
  SelectedAssetsList = 9,
  SectionHeader = 10,
  TableRow = 11,
  Table = 12,
  Timeline = 13
}

export interface TemplateControlAction {
  actionType: ControlActionType;
  controlType: TemplateControlType;
  sourceElementId?: string;
}

export enum ControlActionType {
  None = 0,
  Add = 1,
  Move = 2
}

export interface Communication {
  uId?: string;
  name?: string;
  description?: string;
  createdOn?: any;
  updatedOn?: any;
  lastEditedByName?: string;
  lastEditedById?: string;
  configuredChannelUId?: string;
  communicationTemplateUId?: string;
  datasetUId?: string;
  documentUId?: string;
  documentChangeUId?: string;
  customSubject?: string;
  customHtmlContent?: string;
  customTextContent?: string;
  customDocument?: Document;
  toDistributionListUIds?: string[];
  ccDistributionListUIds?: string[];
  bccDistributionListUIds?: string[];
  toCustomRecipients?: string[];
  ccCustomRecipients?: string[];
  bccCustomRecipients?: string[];
  toUserUIds?: string[];
  ccUserUIds?: string[];
  bccUserUIds?: string[];
  status?: CommunicationStatus;
  communicationStartTime?: any;
  communicationCompletionTime?: any;
  renderedTemplate?: RenderedTemplate;
  sendAsGroup?: boolean;
  settings?: any;
  mapUId?: string;
  toMassCommsRecipients?: MassCommsContact[];
  massNotificationUId?: string;
  renderedContent?: string;
  sendDocumentAttachments?: boolean;
  pathToMap?: string;
}

export interface RenderedTemplate {
  subject?: string;
  htmlContent?: string;
  textContent?: string;
}

export enum CommunicationStatus {
  None = 0,
  Pending = 1,
  Started = 2,
  Completed = 3,
  Cancelled = 4
}

export interface CommunicationActivity {
  channelUId?: string;
  recipientCount?: number;
  templateName?: string;
  lastEditedById?: string;
  lastEditedByName?: string;
  updatedOn?: any;
  communicationUId?: string;
  documentUId?: string;
  configuredName?: string;
}

export enum CommunicationRecipientStatus {
  None = 0,
  Pending = 1,
  Sent = 2,
  Bounced = 3,
  Error = 4
}

export enum RecipientType {
  NotSet = 0,
  To = 1,
  Cc = 2,
  Bcc = 3
}
export interface CommunicationRecipientActivity {
  uId: string;
  name?: string;
  recipient?: string;
  sentTime?: any;
  status: CommunicationRecipientStatus;
  recipientType: RecipientType;
  distributionListUId?: string;
  distributionListName?: string;
}

export interface EverbridgeTemplate {
  configuredChannelUId?: string;
  id: number | null;
  title?: string;
  textMessage?: string;
  textSmsMessage?: string;
}

export interface EverbridgeReplaceTokensRequest {
  templateDto: EverbridgeTemplate;
  documentDto: Document;
}

export interface MassCommsContact {
  contactId?: string;
  name?: string;
}

export interface MassCommsContactWithDistance {
  contactId?: string;
  name?: string;
  distance: number | null;
  distanceText: string | null;
}

export interface MassCommsContactDataset {
  contactsDatasetUId?: string;
  contactIdFieldUId?: string;
}
